import React, { useEffect, useState, useRef } from 'react';
import { string, object, oneOfType, func, array, number, bool } from 'prop-types';
import uniqBy from 'lodash/uniqBy';
import Input from 'ps-components/Input/Input';
import ReactSelect from 'ps-components/ReactSelect/ReactSelect';
import classnames from 'classnames';
import './AdvancedSearch.scss';

const ENTER_KEY_CODE = 13;

AdvancedSearch.propTypes = {
  className: oneOfType([string, object]),
  searchColumnsOnly: func,
  search: func,
  availableFields: array,
  clearAdvancedFilters: func,
  setAdvancedFilters: func,
  selectedViewPk: oneOfType([number, string]),
  currentAdvancedFilters: array,
  currency: string,
  setFacetCurrency: func,
  clearGrid: func,
  defaultField: string,
  shouldInitialLoad: bool,
  localFiltersKey: string
};

export default function AdvancedSearch(props) {
  const firstUpdateFirst = useRef(true);

  const operatorOptions = [
    { label: 'Contains', value: 'like' },
    { label: 'Equals', value: 'eq' },
    { label: 'Does not Equal', value: 'neq' },
    { label: 'Is Empty', value: 'isNull' },
    { label: 'Does not Contain', value: 'nlike' },
    { label: 'Starts with', value: 'start' },
    { label: 'Ends with', value: 'end' }
  ].sort(sortByLabel);

  // create a list of unique fields by the 'term' field
  // and filter out any fields that are _default terms and don't have a displayName
  let uniqueFields = uniqBy(props.availableFields, 'fieldName').filter(field => {
    if (field.term.includes('_default')) {
      if (field.displayName) return true;

      return false;
    }

    return true;
  });

  // create a list of label options
  const fieldOptions = uniqueFields
    .map(term => {
      return {
        label: term.name?.includes('${') ? term.displayName : term.displayName || term.name,
        value: term
      };
    })
    .sort(sortByLabel);

  const initialFilter = {
    field: fieldOptions?.find(field => field.value?.fieldName === props.defaultField),
    operator: operatorOptions.find(op => op.value === 'like'),
    value: null,
    canDelete: false
  };
  const [advancedSearchFilters, setAdvancedSearchFilters] = useState([{ ...initialFilter }]);

  useEffect(() => {
    if (props.currentAdvancedFilters?.length <= 0) setAdvancedSearchFilters([{ ...initialFilter }]);
  }, [fieldOptions?.length]);

  useEffect(() => {
    if (props.currentAdvancedFilters.length > 0) setAdvancedSearchFilters(props.currentAdvancedFilters);
  }, [props.currentAdvancedFilters]);

  useEffect(() => {
    if (!props.selectedViewPk) return;
    props.setAdvancedFilters([]);
    const foundFilters = JSON.parse(localStorage.getItem(props.localFiltersKey + props.selectedViewPk));
    if (foundFilters && foundFilters.length > 0) {
      setAdvancedSearchFilters(foundFilters);
      props.setAdvancedFilters(foundFilters).then(() => searchCall(foundFilters));
    } else {
      setAdvancedSearchFilters([{ ...initialFilter }]);
      props.setAdvancedFilters([]).then(props.shouldInitialLoad && searchCall);

      if (props.searchColumnsOnly) {
        props.searchColumnsOnly();
      }
    }
    return () => {
      props.clearGrid();
      props.clearAdvancedFilters();
    };
  }, [props.selectedViewPk]);

  useEffect(() => {
    if (firstUpdateFirst.current) {
      firstUpdateFirst.current = false;
      return;
    }
    if(props.setFacetCurrency){
      props.setFacetCurrency(props.currency).then(searchCall);
    }
  }, [props.currency]);

  function sortByLabel(first, second) {
    var orderBool = first.label > second.label;
    return orderBool ? 1 : -1;
  }

  function addSearchEntry() {
    setAdvancedSearchFilters(
      advancedSearchFilters.concat([
        {
          field: fieldOptions?.find(field => field.value?.fieldName === props.defaultField),
          operator: operatorOptions.find(op => op.value === 'like'),
          value: null,
          canDelete: true
        }
      ])
    );
  }

  function searchCall(filters) {
    let filtersToCheck;
    if (filters?.filters) {
      filtersToCheck = [...filters.filters];
    } else if (filters) {
      filtersToCheck = [...filters];
    } else {
      filtersToCheck = [...advancedSearchFilters];
    }

    // only search if there is at least one valid filter
    if (filtersToCheck.some(f => f.value !== null) || (props.shouldInitialLoad && filtersToCheck.length === 0)) {
      props.search('');
    }
  }

  function submitSearch() {
    props.setAdvancedFilters(advancedSearchFilters, props.localFiltersKey + props.selectedViewPk).then(searchCall);
  }

  function removeFilter(index) {
    const newFilters = advancedSearchFilters.filter((el, idx) => idx !== index);
    setAdvancedSearchFilters(newFilters);
    localStorage.setItem(props.localFiltersKey + props.selectedViewPk, JSON.stringify(newFilters));
    props.setAdvancedFilters(newFilters).then(searchCall);
  }

  function updateComponent(filters = advancedSearchFilters) {
    setAdvancedSearchFilters([...filters]);
  }

  function clearAllFilters() {
    const newFilters = [
      {
        field: fieldOptions?.find(field => field.value?.fieldName === props.defaultField),
        operator: operatorOptions.find(op => op.value === 'like'),
        value: null,
        canDelete: false
      }
    ];
    setAdvancedSearchFilters(newFilters);
    localStorage.removeItem(props.localFiltersKey + props.selectedViewPk);
    props.clearAdvancedFilters().then(() => {
      if (props.shouldInitialLoad) {
        searchCall();
      } else {
        props.clearGrid();
      }
    });
  }

  const showClearAll = props.currentAdvancedFilters.length > 0;

  return (
    <div className={classnames(props.className, 'facet__advanced-search-container')}>
      <div className="advanced-search__clear-all-container">
        {showClearAll && (
          <a title="Clear all filters" className="advanced-search__clear-all" onClick={clearAllFilters}>
            <i className="icon-cw" />
            Clear All
          </a>
        )}
      </div>
      <div className="advanced-search__filters-container">
        {advancedSearchFilters.map((filter, index) => (
          <AdvancedSearchEntry
            key={index}
            index={index}
            entity={filter}
            fieldOptions={fieldOptions}
            operatorOptions={operatorOptions}
            removeFilter={removeFilter}
            updateComponent={updateComponent}
            search={submitSearch}
          />
        ))}
      </div>
      <div className="facet__advanced-search-bottom-text">
        <a title="Add a new filter" className="advanced-search__add" onClick={addSearchEntry}>
          + Add
        </a>
        <a
          title="Search on the specified filters"
          className="pxl-btn pxl-btn-default advanced-search__search"
          onClick={submitSearch}
        >
          Search
        </a>
      </div>
    </div>
  );
}

AdvancedSearchEntry.propTypes = {
  fieldOptions: array,
  operatorOptions: array,
  index: number,
  entity: object,
  removeFilter: func,
  updateComponent: func,
  search: func
};

function AdvancedSearchEntry(props) {
  function handleFieldChange(field) {
    props.entity.field = field;
    props.updateComponent();
  }

  function handleOperatorChange(operator) {
    props.entity.operator = operator;
    if (operator.value == 'isNull') props.entity.value = ' ';
    props.updateComponent();
  }

  function handleValueChange(e) {
    props.entity.value = e.target.value;
    props.updateComponent();
  }

  function handleRemoveFilter() {
    props.removeFilter(props.index);
  }

  function handleKeyDown(e) {
    const keyCode = e.keyCode;
    if (keyCode === ENTER_KEY_CODE) props.search();
  }

  return (
    <div className="facet__advanced-search-entry">
      <div className={classnames('advanced-search__divider', { 'top-filter': !props.entity.canDelete })}></div>
      {props.entity.canDelete && (
        <i title="Delete this filter" onClick={handleRemoveFilter} className="advanced-search__delete icon-trash" />
      )}
      <ReactSelect
        className="search-entry__input"
        value={props.entity.field}
        options={props.fieldOptions}
        onChange={handleFieldChange}
        menuPosition={'fixed'}
      />
      <ReactSelect
        className="search-entry__input"
        value={props.entity.operator}
        options={props.operatorOptions}
        onChange={handleOperatorChange}
        menuPosition={'fixed'}
      />
      {(props.entity.operator === null || props.entity.operator?.value !== 'isNull') && (
        <Input
          className="search-entry__input pxl-form-control"
          value={props.entity.value || ''}
          onChange={handleValueChange}
          onKeyDown={handleKeyDown}
        />
      )}
    </div>
  );
}
